import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectUser, selectUserType, logoutUser } from '../../redux/slices/authSlice';
import {
  setSelectedTab,
  selectSelectedTab,
  selectLawyerDashboard,
} from '../../redux/slices/lawyerDashboardSlice';

const LawyerDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const userType = useSelector(selectUserType);
  const selectedTab = useSelector(selectSelectedTab);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Función para hacer logout
  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      navigate('/');
    } catch (error) {
      console.error('Error al hacer logout:', error);
    }
  };

  // Función para cambiar de tab
  const handleTabChange = tab => {
    dispatch(setSelectedTab(tab));
  };

  useEffect(() => {
    // Aquí puedes cargar datos específicos del abogado
    if (user) {
      console.log('Dashboard cargado para abogado:', user.name);
      // TODO: Cargar datos del dashboard (citas, consultas, estadísticas)
    }
  }, [user, dispatch]);

  // Datos mock basados en el diseño
  const mockData = {
    stats: {
      totalCitas: 7265,
      proximasCitas: 7265,
      reclamaciones: 7265,
    },
    puntos: 3599,
    casosCompletados: 25,
    clicks: 7265,
    vistasAlPerfil: 7265,
    tiempoPromedio: 25,
  };

  // Componente de navegación por tabs
  const TabNavigation = () => (
    <div className="bg-white border-b border-gray-200">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex space-x-8">
          {[
            { id: 'inicio', label: 'Inicio' },
            { id: 'encuentraAbogado', label: 'Encuentra un Abogado' },
            { id: 'consultaExpress', label: 'Consulta Express' },
            { id: 'estadisticas', label: 'Estadísticas de Citas' },
            { id: 'perfil', label: 'Perfil' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );

  // Componente del header del perfil
  const ProfileHeader = () => (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{user?.name || 'Mariano Duran'}</h2>
            <div className="flex items-center text-sm text-gray-600">
              <svg className="w-4 h-4 text-yellow-400 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              5.0
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-2 text-gray-400 hover:text-gray-500"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 17h5l-5-5 5-5v10z"
              />
            </svg>
            <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-400"></span>
          </button>
        </div>
      </div>
    </div>
  );

  // Componente de estadísticas principales
  const StatsCards = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <div className="bg-blue-600 rounded-lg p-6 text-white">
        <h3 className="text-sm font-medium opacity-90">Total de Citas</h3>
        <p className="text-3xl font-bold mt-2">{mockData.stats.totalCitas.toLocaleString()}</p>
      </div>
      <div className="bg-blue-700 rounded-lg p-6 text-white">
        <h3 className="text-sm font-medium opacity-90">Próximas Citas</h3>
        <p className="text-3xl font-bold mt-2">{mockData.stats.proximasCitas.toLocaleString()}</p>
      </div>
      <div className="bg-blue-800 rounded-lg p-6 text-white">
        <h3 className="text-sm font-medium opacity-90">Reclamaciones</h3>
        <p className="text-3xl font-bold mt-2">{mockData.stats.reclamaciones.toLocaleString()}</p>
      </div>
    </div>
  );

  // Componente de puntos Iustia
  const PointsSection = () => (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
      <div className="text-center">
        <div className="flex justify-between items-center mb-4">
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">
            Tus Puntos IUSTIA
          </button>
          <button className="px-4 py-2 text-blue-600 border border-blue-600 rounded-lg text-sm">
            Genera Puntos Iustia
          </button>
        </div>
        <div className="text-4xl font-bold text-gray-900 mb-2">
          {mockData.puntos.toLocaleString()}
        </div>
        <div className="text-sm text-gray-600 mb-4">
          Puntos <span className="font-bold">iustia</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
          <div className="bg-blue-600 h-2 rounded-full" style={{ width: '70%' }}></div>
        </div>
        <div className="text-left">
          <div className="text-lg font-semibold text-gray-900">{mockData.casosCompletados}</div>
          <div className="text-sm text-gray-600">
            Casos Completados | 30 casos más para subir de Status
          </div>
        </div>
      </div>
    </div>
  );

  // Componente de métricas adicionales
  const AdditionalMetrics = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-2xl font-bold text-gray-900">{mockData.clicks.toLocaleString()}</p>
            <p className="text-sm text-gray-600">Clicks</p>
          </div>
          <div className="text-green-500 text-sm">+11.01%</div>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-2xl font-bold text-gray-900">
              {mockData.vistasAlPerfil.toLocaleString()}
            </p>
            <p className="text-sm text-gray-600">Vistas al Perfil</p>
          </div>
          <div className="text-green-500 text-sm">+11.01%</div>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-2xl font-bold text-gray-900">{mockData.tiempoPromedio} min</p>
            <p className="text-sm text-gray-600">Tiempo Promedio del Usuario en Perfil</p>
          </div>
          <div className="text-green-500 text-sm">+11.01%</div>
        </div>
      </div>
    </div>
  );

  // Componente de calendario
  const CalendarComponent = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

    const monthNames = [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre',
    ];

    const dayNames = ['DOM', 'LUN', 'MAR', 'MIE', 'JUE', 'VIE', 'SAB'];

    const renderCalendarDays = () => {
      const days = [];

      // Días vacíos del mes anterior
      for (let i = 0; i < firstDayOfMonth; i++) {
        days.push(<div key={`empty-${i}`} className="h-8"></div>);
      }

      // Días del mes actual
      for (let day = 1; day <= daysInMonth; day++) {
        const isToday = day === currentDate.getDate();
        days.push(
          <button
            key={day}
            className={`h-8 w-8 text-sm rounded-full hover:bg-blue-100 ${
              isToday ? 'bg-blue-600 text-white' : 'text-gray-900'
            }`}
          >
            {day}
          </button>
        );
      }

      return days;
    };

    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {monthNames[currentMonth]} {currentYear}
          </h3>
          <div className="flex space-x-2">
            <button className="p-1 hover:bg-gray-100 rounded">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <button className="p-1 hover:bg-gray-100 rounded">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map(day => (
            <div
              key={day}
              className="h-8 flex items-center justify-center text-xs font-medium text-gray-500"
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">{renderCalendarDays()}</div>

        <button className="w-full mt-4 py-2 px-4 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
          + Confirmar Fecha
        </button>
      </div>
    );
  };

  // Componente de horarios
  const ScheduleComponent = () => {
    const timeSlots = [
      '8:00',
      '8:30',
      '9:00',
      '9:30',
      '10:00',
      '10:30',
      '11:00',
      '11:30',
      '12:00',
      '12:30',
      '13:00',
      '13:30',
      '14:00',
      '14:30',
      '15:00',
      '15:30',
      '16:00',
      '16:30',
      '17:00',
      '17:30',
      '18:00',
      '18:30',
      '19:00',
      '19:30',
      '20:00',
      '20:30',
      '21:00',
      '21:30',
    ];

    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="mb-4">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Horarios</h3>
          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700">Zona Horaria</label>
            <select className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-sm">
              <option>Zona Centro (10:30 am)</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2 mb-4">
          {timeSlots.map(time => (
            <button
              key={time}
              className="py-2 px-3 border border-gray-300 rounded text-sm hover:bg-blue-50 hover:border-blue-300"
            >
              {time}
            </button>
          ))}
        </div>

        <button className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
          + Confirmar Horarios
        </button>
      </div>
    );
  };

  // Componente de notificaciones
  const NotificationsPanel = () => {
    if (!showNotifications) return null;

    return (
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex space-x-4">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">
              Recibir Notificaciones
            </button>
            <button className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg text-sm">
              Apagar Notificaciones
            </button>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-gray-300 rounded-full flex-shrink-0"></div>
            <div className="flex-1">
              <h4 className="font-medium text-gray-900">Solicitud de Consulta Express</h4>
              <p className="text-sm text-gray-600 mt-1">Nombre del Cliente</p>
              <p className="text-sm text-gray-600">Miércoles 11 de Junio de 2025</p>
              <p className="text-sm text-gray-600">10:00 a.m. (Zona Centro)</p>
              <button className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                + Aceptar Consulta
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (selectedTab) {
      case 'inicio':
        return (
          <>
            <StatsCards />
            <PointsSection />
            <AdditionalMetrics />
            <div className="text-center text-sm text-gray-500 mt-8">
              Miembro desde: 30 de Junio 2025
            </div>
          </>
        );
      case 'encuentraAbogado':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <CalendarComponent />
            <ScheduleComponent />
          </div>
        );
      case 'consultaExpress':
        return <NotificationsPanel />;
      case 'estadisticas':
        return <AdditionalMetrics />;
      case 'perfil':
        return (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Configuración del Perfil</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b">
                <span className="text-gray-700">Editar Perfil</span>
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
              <div className="flex items-center justify-between py-3 border-b">
                <span className="text-gray-700">Datos Bancarios</span>
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
              <div className="flex items-center justify-between py-3 border-b">
                <span className="text-gray-700">Aviso de Privacidad</span>
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
              <div className="flex items-center justify-between py-3">
                <span className="text-gray-700">Términos y Condiciones</span>
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </div>
          </div>
        );
      default:
        return <StatsCards />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header con título principal */}
          <div className="py-6 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">
              ¡Bienvenido {user?.name || 'Mariano'}!
            </h1>
          </div>
        </div>
      </div>

      {/* Navegación por tabs */}
      <TabNavigation />

      {/* Contenido Principal */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header del perfil */}
        <ProfileHeader />

        {/* Notificaciones (solo si están habilitadas) */}
        {showNotifications && <NotificationsPanel />}

        {/* Contenido dinámico basado en el tab seleccionado */}
        {renderTabContent()}

        {/* Botón de logout al final */}
        <div className="mt-12 text-center">
          <button
            onClick={handleLogout}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            Cerrar Sesión
          </button>
        </div>
      </main>
    </div>
  );
};

export default LawyerDashboard;
